import React from 'react';
import type { ProfileComponent } from '../../../types/builder';

interface ProfileRendererProps {
  component: ProfileComponent;
}

export const ProfileRenderer: React.FC<ProfileRendererProps> = ({ component }) => {
  const { avatarUrl, avatarSize, backgroundColor, alignment, padding, style } = component.settings;

  const profileStyle = style || 'default';
  const alignmentStyles = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };

  // Default Style
  if (profileStyle === 'default') {
    return (
      <div style={{ padding: `${padding}px`, backgroundColor, display: 'flex', flexDirection: 'column', alignItems: alignmentStyles[alignment], width: '100%' }}>
        <div style={{ width: `${avatarSize}px`, height: `${avatarSize}px`, borderRadius: '50%', overflow: 'hidden', border: '4px solid rgba(255, 255, 255, 0.9)', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}>
          <img 
            src={avatarUrl} 
            alt="Profile" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
            onError={(e) => { 
              (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=Profile&size=300&background=667eea&color=fff'; 
            }} 
          />
        </div>
      </div>
    );
  }

  // Card Style
  if (profileStyle === 'card') {
    return (
      <div style={{ padding: `${padding}px`, backgroundColor }} className="flex justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8" style={{ display: 'flex', flexDirection: 'column', alignItems: alignmentStyles[alignment] }}>
          <div style={{ width: `${avatarSize}px`, height: `${avatarSize}px`, borderRadius: '50%', overflow: 'hidden', border: '4px solid rgba(99, 102, 241, 0.3)', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)' }}>
            <img 
              src={avatarUrl} 
              alt="Profile" 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
              onError={(e) => { 
                (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=Profile&size=300&background=667eea&color=fff'; 
              }} 
            />
          </div>
        </div>
      </div>
    );
  }

  // Minimal Style
  if (profileStyle === 'minimal') {
    return (
      <div style={{ padding: `${padding}px`, backgroundColor, display: 'flex', flexDirection: 'column', alignItems: alignmentStyles[alignment], width: '100%' }}>
        <div style={{ width: `${avatarSize}px`, height: `${avatarSize}px`, borderRadius: '50%', overflow: 'hidden', border: '2px solid rgba(200, 200, 200, 0.3)' }}>
          <img 
            src={avatarUrl} 
            alt="Profile" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
            onError={(e) => { 
              (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=Profile&size=300&background=667eea&color=fff'; 
            }} 
          />
        </div>
      </div>
    );
  }

  // Modern Style
  if (profileStyle === 'modern') {
    return (
      <div style={{ padding: `${padding}px`, backgroundColor }} className="flex justify-center">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-blue-600/20 blur-3xl"></div>
          <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 border border-purple-500/30 shadow-2xl" style={{ display: 'flex', flexDirection: 'column', alignItems: alignmentStyles[alignment] }}>
            <div style={{ width: `${avatarSize}px`, height: `${avatarSize}px`, borderRadius: '50%', overflow: 'hidden', border: '4px solid rgba(139, 92, 246, 0.5)', boxShadow: '0 0 30px rgba(139, 92, 246, 0.5)' }}>
              <img 
                src={avatarUrl} 
                alt="Profile" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                onError={(e) => { 
                  (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=Profile&size=300&background=667eea&color=fff'; 
                }} 
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return null;
};
